import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BreadcrumbTwo from '../../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../../components/DataTable/BasicPagination';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';
import SectionWrapper from '../../../../components/SectionWrapper/SectionWrapper';
import useRestaurantMenuConfiguration from '../../../../hooks/restaurant/useRestaurantMenuConfiguration';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../../../layout/WorkspaceLayout';
import CombosTable from './components/CombosTable';
import ComboModal from './components/ComboModal';
import DishModifierAssignmentTable from './components/DishModifierAssignmentTable';
import MenuConfigurationBranchEmpty from './components/MenuConfigurationBranchEmpty';
import MenuItemModifiersModal from '../MenuItemModifiersModal';
import MenuConfigurationHeader from './components/MenuConfigurationHeader';
import MenuConfigurationLoading from './components/MenuConfigurationLoading';
import MenuConfigurationTabs from './components/MenuConfigurationTabs';
import ModifierGroupsTable from './components/ModifierGroupsTable';
import ModifierGroupModal from './components/ModifierGroupModal';
import RestaurantCatalogExtensionService from '../../../../services/restaurant/RestaurantCatalogExtensionService';
import {
  RestaurantComboDTO,
  RestaurantModifierGroupDTO,
} from '../../../../types/restaurant/RestaurantCatalogExtensionTypes';

const RestaurantMenuConfigurationScreen = () => {
  const configuration = useRestaurantMenuConfiguration();
  const [modifierSearch, setModifierSearch] = useState('');
  const [comboSearch, setComboSearch] = useState('');
  const [assignmentDish, setAssignmentDish] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [groupModal, setGroupModal] = useState<{
    open: boolean;
    group: RestaurantModifierGroupDTO | null;
  }>({ open: false, group: null });
  const [comboModal, setComboModal] = useState<{
    open: boolean;
    combo: RestaurantComboDTO | null;
  }>({ open: false, combo: null });

  const toggleComboActive = async (combo: RestaurantComboDTO) => {
    try {
      await RestaurantCatalogExtensionService.updateCombo(
        Number(configuration.branchId),
        combo.id,
        {
          code: combo.code,
          name: combo.name,
          description: combo.description,
          price: combo.price,
          available: combo.available,
          displayOrder: combo.displayOrder,
          active: !combo.active,
          items: combo.items
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item, index) => ({
              menuItemId: item.menuItemId,
              quantity: item.quantity,
              displayOrder: index,
            })),
        }
      );
    } finally {
      configuration.reload();
    }
  };

  const toggleGroupActive = async (group: RestaurantModifierGroupDTO) => {
    try {
      await RestaurantCatalogExtensionService.updateModifierGroup(
        Number(configuration.branchId),
        group.id,
        {
          name: group.name,
          description: group.description,
          minSelections: group.minSelections,
          maxSelections: group.maxSelections,
          required: group.required,
          displayOrder: group.displayOrder,
          active: !group.active,
        }
      );
      configuration.reload();
    } catch {
      /* el handler global del hook no aplica aquí; recarga y ya */
      configuration.reload();
    }
  };
  const debouncedModifierSearch = useDebouncedValue(modifierSearch, 350);
  const debouncedComboSearch = useDebouncedValue(comboSearch, 350);
  const navigate = useNavigate();

  useEffect(() => {
    configuration.setModifierGroupFilters((current) => ({
      ...current,
      search: debouncedModifierSearch.trim() || undefined,
      page: 0,
    }));
    configuration.setModifierFilters((current) => ({
      ...current,
      search: debouncedModifierSearch.trim() || undefined,
      page: 0,
    }));
  }, [debouncedModifierSearch]);

  useEffect(() => {
    configuration.setComboFilters((current) => ({
      ...current,
      search: debouncedComboSearch.trim() || undefined,
      page: 0,
    }));
  }, [debouncedComboSearch]);
  const changeTab = (tab: typeof configuration.tab) => {
    configuration.setTab(tab);
  };

  if (
    !configuration.loadingBranches &&
    !configuration.branchError &&
    !configuration.branchId
  ) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />
        <SectionWrapper>
          <MenuConfigurationBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <MenuConfigurationHeader
          branches={configuration.branches}
          branchId={configuration.branchId}
          loadingBranches={configuration.loadingBranches}
          branchError={configuration.branchError}
          canPickBranch={configuration.canPickBranch}
          onBranchChange={configuration.setBranchId}
          onBack={() => navigate('/restaurant/menu/items')}
        />

        <MenuConfigurationTabs
          value={configuration.tab}
          disabled={!configuration.branchId || configuration.loading}
          onChange={changeTab}
        />

        {configuration.tab === 'MODIFIERS' && (
          <div className="grid gap-4 border-b border-stroke p-5 dark:border-strokedark md:grid-cols-2 md:items-end">
            <SearchInputField
              label="Buscar"
              name="modifierSearch"
              value={modifierSearch}
              iconKey="search"
              placeholder="Nombre del grupo..."
              withMargin={false}
              onChange={(event) => setModifierSearch(event.target.value)}
            />
            <SelectField
              label="Estado"
              name="modifierActiveFilter"
              value={
                configuration.modifierGroupFilters.active == null
                  ? 'ALL'
                  : configuration.modifierGroupFilters.active
                  ? 'ACTIVE'
                  : 'INACTIVE'
              }
              iconKey="status"
              options={[
                { value: 'ALL', label: 'Todos' },
                { value: 'ACTIVE', label: 'Activos' },
                { value: 'INACTIVE', label: 'Inactivos' },
              ]}
              onChange={(event) => {
                const active =
                  event.target.value === 'ALL'
                    ? undefined
                    : event.target.value === 'ACTIVE';
                configuration.setModifierGroupFilters((current) => ({
                  ...current,
                  active,
                  page: 0,
                }));
              }}
            />
          </div>
        )}

        {configuration.tab === 'COMBOS' && (
          <div className="grid gap-4 border-b border-stroke p-5 dark:border-strokedark md:grid-cols-3 md:items-end">
            <SearchInputField
              label="Buscar"
              name="comboSearch"
              value={comboSearch}
              iconKey="search"
              placeholder="Código o nombre..."
              withMargin={false}
              onChange={(event) => setComboSearch(event.target.value)}
            />
            <SelectField
              label="Estado"
              name="comboActiveFilter"
              value={configuration.comboFilters.active == null ? 'ALL' : configuration.comboFilters.active ? 'ACTIVE' : 'INACTIVE'}
              options={[{ value: 'ALL', label: 'Todos' }, { value: 'ACTIVE', label: 'Activos' }, { value: 'INACTIVE', label: 'Inactivos' }]}
              onChange={(event) => configuration.setComboFilters((current) => ({ ...current, active: event.target.value === 'ALL' ? undefined : event.target.value === 'ACTIVE', page: 0 }))}
            />
            <SelectField
              label="Disponibilidad"
              name="comboAvailableFilter"
              value={configuration.comboFilters.available == null ? 'ALL' : configuration.comboFilters.available ? 'AVAILABLE' : 'UNAVAILABLE'}
              options={[{ value: 'ALL', label: 'Todos' }, { value: 'AVAILABLE', label: 'Disponibles' }, { value: 'UNAVAILABLE', label: 'No disponibles' }]}
              onChange={(event) => configuration.setComboFilters((current) => ({ ...current, available: event.target.value === 'ALL' ? undefined : event.target.value === 'AVAILABLE', page: 0 }))}
            />
          </div>
        )}

        {configuration.tab === 'ASSIGNMENTS' && (
          <div className="grid gap-4 border-b border-stroke p-5 dark:border-strokedark md:grid-cols-2 md:items-end">
            <div className="md:pb-5">
              <SearchInputField label="Buscar plato" name="assignmentMenuSearch" value={configuration.menuSearch} iconKey="search" placeholder="Nombre o código..." withMargin={false} onChange={(event) => { configuration.setMenuSearch(event.target.value); configuration.setMenuPage(0); }} />
            </div>
            <SelectField
              label="Categoría"
              name="assignmentMenuCategory"
              value={configuration.menuCategoryId}
              iconKey='list'
              options={[
                { value: 0, label: 'Todas las categorías' },
                ...configuration.menuCategories.map((category) => ({ value: category.id, label: category.name })),
              ]}
              onChange={(event) => { configuration.setMenuCategoryId(Number(event.target.value)); configuration.setMenuPage(0); }}
            />
          </div>
        )}

        {configuration.loading ? (
          <MenuConfigurationLoading />
        ) : configuration.tab === 'MODIFIERS' ? (
          <ModifierGroupsTable
            groups={configuration.groups}
            onCreate={() => setGroupModal({ open: true, group: null })}
            onEdit={(group) => setGroupModal({ open: true, group })}
            onToggleActive={toggleGroupActive}
          />
        ) : configuration.tab === 'COMBOS' ? (
          <CombosTable
            combos={configuration.combos}
            onCreate={() => setComboModal({ open: true, combo: null })}
            onEdit={(combo) => setComboModal({ open: true, combo })}
            onToggleActive={toggleComboActive}
          />
        ) : configuration.tab === 'ASSIGNMENTS' ? (
          <DishModifierAssignmentTable
            menuItems={configuration.menuItems}
            onConfigure={(dish) =>
              setAssignmentDish({
                id: dish.id,
                name: dish.displayName || dish.productName,
              })
            }
          />
        ) : null}

        {configuration.tab === 'MODIFIERS' && (
          <BasicPagination
            currentPage={configuration.groupPage.number}
            totalPages={configuration.groupPage.totalPages}
            totalElements={configuration.groupPage.totalElements}
            size={configuration.groupPage.size}
            first={configuration.groupPage.number === 0}
            last={
              configuration.groupPage.number >=
              configuration.groupPage.totalPages - 1
            }
            onPageChange={(page) =>
              configuration.setModifierGroupFilters((current) => ({
                ...current,
                page,
              }))
            }
            onSizeChange={(size) =>
              configuration.setModifierGroupFilters((current) => ({
                ...current,
                page: 0,
                size,
              }))
            }
          />
        )}
        {configuration.tab === 'COMBOS' && (
          <BasicPagination
            currentPage={configuration.comboPage.number}
            totalPages={configuration.comboPage.totalPages}
            totalElements={configuration.comboPage.totalElements}
            size={configuration.comboPage.size}
            first={configuration.comboPage.number === 0}
            last={configuration.comboPage.number >= configuration.comboPage.totalPages - 1}
            onPageChange={(page) => configuration.setComboFilters((current) => ({ ...current, page }))}
            onSizeChange={(size) => configuration.setComboFilters((current) => ({ ...current, page: 0, size }))}
          />
        )}
        {configuration.tab === 'ASSIGNMENTS' && (
          <BasicPagination
            currentPage={configuration.menuItemPage.number}
            totalPages={configuration.menuItemPage.totalPages}
            totalElements={configuration.menuItemPage.totalElements}
            size={configuration.menuItemPage.size}
            first={configuration.menuItemPage.number === 0}
            last={configuration.menuItemPage.number >= configuration.menuItemPage.totalPages - 1}
            onPageChange={configuration.setMenuPage}
            onSizeChange={(size) => { configuration.setMenuSize(size); configuration.setMenuPage(0); }}
          />
        )}
      </SectionWrapper>

      <MenuItemModifiersModal
        open={assignmentDish != null}
        menuItemId={assignmentDish?.id ?? null}
        menuItemName={assignmentDish?.name}
        branchId={Number(configuration.branchId) || 0}
        onClose={() => setAssignmentDish(null)}
      />

      <ModifierGroupModal
        open={groupModal.open}
        branchId={Number(configuration.branchId) || 0}
        group={groupModal.group}
        rawMaterials={configuration.rawMaterials}
        units={configuration.units}
        onClose={() => setGroupModal({ open: false, group: null })}
        onSaved={configuration.reload}
      />

      <ComboModal
        open={comboModal.open}
        branchId={Number(configuration.branchId) || 0}
        combo={comboModal.combo}
        menuItems={configuration.menuItems}
        menuItemCache={configuration.menuItemCache}
        menuSearch={configuration.menuSearch}
        onMenuSearchChange={(value) => {
          configuration.setMenuSearch(value);
          configuration.setMenuPage(0);
        }}
        onClose={() => setComboModal({ open: false, combo: null })}
        onSaved={configuration.reload}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantMenuConfigurationScreen;
